// Settings → "What the Director remembers" (spec-v3-formats.md): the notes it keeps about you and your work (what you
// make, who for, the looks you like), each one editable and deletable, plus "Forget everything". The Director's own
// remember/forget tools send `memory_changed`, which refreshes this list.
import { useState, type FormEvent } from "react";
import { api } from "../api";
import { director } from "../director";
import { toast, useRefresh } from "../store";
import type { Memory, MemoryKind } from "../types";
import { ErrorNote, Icon, Sheet, useLoad } from "./bits";

export const KIND_LABEL: Record<MemoryKind, string> = { goal: "Goal", audience: "Audience", style: "Style", format: "Type", voice: "Voice", brand: "Brand", preference: "Preference", note: "Note" };
const KINDS = Object.keys(KIND_LABEL) as MemoryKind[];

export function MemoryPanel() {
  const tick = useRefresh("memory");
  const { data, setData, error, loading, reload } = useLoad<Memory[]>(() => api.memory(), [tick]);
  const [editing, setEditing] = useState<Memory | "new" | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const items = data ?? [];

  const remove = async (m: Memory) => {
    const prev = items;
    setData(items.filter((x) => x.id !== m.id));
    try {
      await api.forget(m.id);
      director.context(`The user deleted a memory in Settings: "${m.text}". Don't bring it up again.`);
    } catch (e) {
      setData(prev);
      toast(`Couldn't delete it. ${(e as Error).message}`, "bad");
    }
  };
  const forgetAll = async () => {
    setBusy(true);
    try {
      await api.forgetAll();
      setData([]);
      setConfirm(false);
      director.context("The user asked you to forget everything you remembered about them (Settings). Start fresh.");
      toast("Done. Your Director starts fresh.", "good");
    } catch (e) {
      toast(`Couldn't clear them. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  const save = async (kind: MemoryKind, text: string, m: Memory | "new") => {
    try {
      if (m === "new") {
        const made = await api.addMemory({ kind, text });
        setData([made, ...items]);
        director.context(`The user added a note for you to remember (Settings): "${text}".`);
      } else {
        const next = await api.editMemory(m.id, text);
        setData(items.map((x) => (x.id === m.id ? { ...next, kind: next.kind ?? m.kind } : x)));
        director.context(`The user corrected a memory (Settings): "${m.text}" is now "${text}".`);
      }
      setEditing(null);
    } catch (e) {
      toast(`Couldn't save it. ${(e as Error).message}`, "bad");
    }
  };

  return (
    <section data-help="settings.memory" className="group memory" aria-labelledby="mem-h">
      <h2 id="mem-h" className="group-title">
        What the Director remembers
      </h2>
      <p className="muted">Short notes it keeps so it can suggest the right thing next time: what you make, who it's for, the looks you like. Nothing else. Change or delete any of them.</p>
      {error && <ErrorNote text={`Couldn't load them. ${error}`} retry={reload} />}
      {loading && !data && (
        <ul className="mem-list" aria-label="Loading">
          {[0, 1, 2].map((i) => (
            <li key={i} className="mem-skel" />
          ))}
        </ul>
      )}
      {data && !items.length && (
        <div className="mem-empty">
          <Icon name="chat" size={20} />
          <p>Nothing yet. As you talk, your Director keeps a few notes about what you make and who it's for. They'll show up here.</p>
        </div>
      )}
      {!!items.length && (
        <ul className="mem-list">
          {items.map((m) => (
            <li key={m.id} className="mem">
              <span className={`mem-kind mem-kind-${m.kind}`}>{KIND_LABEL[m.kind]}</span>
              <p className="mem-text">{m.text}</p>
              <span className="mem-acts">
                <button data-action="memory.edit" type="button" className="icon-btn" aria-label={`Edit: ${m.text}`} onClick={() => setEditing(m)}>
                  <Icon name="edit" size={16} />
                </button>
                <button data-action="memory.forget" type="button" className="icon-btn" aria-label={`Delete: ${m.text}`} onClick={() => void remove(m)}>
                  <Icon name="trash" size={16} />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="mem-foot">
        <button data-action="memory.add" type="button" className="btn btn-quiet btn-small" onClick={() => setEditing("new")}>
          <Icon name="plus" size={16} /> Add a note
        </button>
        {!!items.length && (
          <button type="button" className="btn btn-quiet btn-small mem-forget" onClick={() => setConfirm(true)}>
            Forget everything
          </button>
        )}
      </div>
      {editing && <MemoryEdit m={editing} onClose={() => setEditing(null)} onSave={(k, t) => save(k, t, editing)} />}
      {confirm && (
        <Sheet title="Forget everything?" onClose={() => setConfirm(false)}>
          <div className="sheet-body">
            <p>
              Your Director deletes all {items.length} {items.length === 1 ? "note" : "notes"} about you and starts fresh. Your videos, brand and library stay as they are. This can't be undone.
            </p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-quiet" onClick={() => setConfirm(false)} data-autofocus>
                Keep them
              </button>
              <button data-action="memory.forget" type="button" className="btn btn-danger" onClick={() => void forgetAll()} disabled={busy}>
                {busy ? "Forgetting" : "Forget everything"}
              </button>
            </div>
          </div>
        </Sheet>
      )}
    </section>
  );
}

function MemoryEdit({ m, onClose, onSave }: { m: Memory | "new"; onClose: () => void; onSave: (kind: MemoryKind, text: string) => Promise<void> }) {
  const [text, setText] = useState(m === "new" ? "" : m.text);
  const [kind, setKind] = useState<MemoryKind>(m === "new" ? "note" : m.kind);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    await onSave(kind, t);
    setBusy(false);
  };
  return (
    <Sheet title={m === "new" ? "Add a note" : "Edit the note"} onClose={onClose}>
      <form className="sheet-body" onSubmit={submit}>
        {m === "new" && (
          <div className="field">
            <span className="field-label">What kind of note</span>
            <span className="bf-chips" role="radiogroup">
              {KINDS.map((k) => (
                <button key={k} type="button" role="radio" aria-checked={k === kind} className={`chip${k === kind ? " is-on" : ""}`} onClick={() => setKind(k)}>
                  {KIND_LABEL[k]}
                </button>
              ))}
            </span>
          </div>
        )}
        <label className="field">
          <span className="field-label">One line your Director should know</span>
          <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 400))} rows={3} placeholder="I make history videos for my YouTube channel, mostly 5 minutes long" data-autofocus />
          <span className="field-hint">{400 - text.length} characters left</span>
        </label>
        <button type="submit" className="btn btn-primary btn-block" disabled={busy || !text.trim()}>
          {busy ? "Saving" : "Save"}
        </button>
      </form>
    </Sheet>
  );
}
